import ChartContainer from "components/molecules/ChartContainer";
import VariableWidthBarChart from "components/molecules/Charts/VariableWidthBarChart";
import { ChartErrorState } from "components/molecules/ErrorStates";
import { ChartNoDataState } from "components/molecules/NoDataState";
import { DATA_KEY_COLORS } from "constants/chartsColors.constant";
import { useMemo } from "react";
import { UseQueryResult } from "react-query";
import { getDisplayInfo } from "utils/formatting";

interface AverageMarginalAbatementCostByMeasureCategoryChartProps {
    query: () => UseQueryResult<AverageMarginalAbatementCostByMeasureCategoryChartData>;
    backgroundColor?: string;
}

function AverageMarginalAbatementCostByMeasureCategoryChart({
    query,
    backgroundColor,
}: AverageMarginalAbatementCostByMeasureCategoryChartProps) {
    const { data, isLoading, error } = query();

    const { formatFunction: formatAbatementCost, unit: abatementCostUnit } = getDisplayInfo("marginalAbatementCost");

    const { formatFunction: formatEmissions, unit: carbonEmissionsUnit } = useMemo(() => {
        const values = data?.map((d) => d.totalLifetimeCarbonEmissionSavings) || [];

        return getDisplayInfo("carbonEmissions", values);
    }, [data]);

    const chartData = useMemo(() => {
        if (!data) return [];
        let cumulativeX = 0;
        const formatted = data
            .sort((a, b) => a.averageMarginalAbatementCost - b.averageMarginalAbatementCost)
            .map((datum, index) => {
                const formattedAbatementCost = formatAbatementCost(datum.averageMarginalAbatementCost);

                const formattedLifetimeCarbonSavings = formatEmissions(datum.totalLifetimeCarbonEmissionSavings, false);

                const value = {
                    y: datum.averageMarginalAbatementCost,
                    x: cumulativeX || 0,
                    width: datum.totalLifetimeCarbonEmissionSavings,
                    color: DATA_KEY_COLORS[datum.carbonReductionMeasureCategory] || chartColors[index],
                    key: datum.carbonReductionMeasureCategory,
                    yLabel: `Cost of abatement: ${formattedAbatementCost} ${abatementCostUnit}`,
                    xLabel: `Lifetime carbon emission savings: ${formattedLifetimeCarbonSavings} ${carbonEmissionsUnit}`,
                };
                cumulativeX += datum.totalLifetimeCarbonEmissionSavings;
                return value;
            });
        return formatted;
    }, [data]);

    if (data === null) return <ChartNoDataState title="Average Marginal Abatement Cost per Measure Type" />;
    if (error) return <ChartErrorState />;

    return (
        <ChartContainer
            title="Average marginal abatement cost per measure type"
            loading={isLoading}
            exportableData={chartData}
        >
            <VariableWidthBarChart
                data={chartData}
                yAxisFormatFunction={(v) => formatAbatementCost(v)}
                xAxisFormatFunction={(v) => formatEmissions(Number(v), false)}
                xAxisLabel="Cost of abatement  $/tCO₂e"
                yAxisLabel={`Lifetime carbon emission savings ${carbonEmissionsUnit}`}
                backgroundColor={backgroundColor}
            />
        </ChartContainer>
    );
}

const chartColors = [
    "#EF81DA",
    "#EB03AD",
    "#D5BBF9",
    "#7A2AEB",
    "#7CB7F2",
    "#066ECC",
    "#4BF7B5",
    "#00BC98",
    "#3B814B",
    "#F9BF73",
    "#F7931E",
    "#BB6E00",
    "#F94646",
    "#CC303C",
];

export default AverageMarginalAbatementCostByMeasureCategoryChart;
